/* eslint-disable react/react-in-jsx-scope */
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  Slider,
  TextField,
  Typography,
  SvgIcon,
} from "@mui/material";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import ViewListRoundedIcon from "@mui/icons-material/ViewListRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import { ReactComponent as Premium } from "../../../assets/icons/premium.svg";
import { ReactComponent as FilterBtnIcon } from "../../../assets/icons/filter-btn-icon.svg";
import { FiltersState } from "./MobileFilters.types";
import { useFilterLogic } from "../../hooks/filters";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { Service } from "../../../features/listing-page/listing-page.types";
import { ReactComponent as ResetIcon } from "../../../assets/icons/reset-icon.svg";
import "./MobileFilters.scss";
import { useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

type FiltersProps = {
  allServices: Service[];
  initialFilters: FiltersState;
  onChange: (filters: FiltersState) => void;
  onCloseDrawer: () => void;
  listView: boolean;
  setListView: React.Dispatch<React.SetStateAction<boolean>>;
  allCategories: any[];
};

export default function MobileFilters({
  allServices,
  allCategories,
  initialFilters,
  onChange,
  onCloseDrawer,
  setListView,
  listView,
}: FiltersProps) {
  const { filters, setFilters, resetFilters } = useFilterLogic(
    initialFilters,
    onChange
  );

  // STATES
  const [activePriceField, setActivePriceField] = useState<
    "min" | "max" | null
  >(null);
  const [activeDistanceField, setActiveDistanceField] = useState<
    "min" | "max" | null
  >(null);

  const [priceSlider, setPriceSlider] = useState(0);
  const [distanceSlider, setDistanceSlider] = useState(0);

  // HOOKS
  const navigate = useNavigate();

  // const filteredServices = useMemo(() => {
  //   if (!filters.needType) {
  //     return allServices;
  //   }
  //   return allServices?.filter((service) =>
  //     service?.needTypes?.includes(filters.needType)
  //   );
  // }, [allServices, filters.needType]);

  return (
    <Box
      className="filters"
      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
    >
      {/* Top bar with breadcrumbs + view toggle */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
          boxShadow: "0px 0px 6px 1px #00000033",
        }}
        className="mobile-breadcrumbs-toggle"
      >
        {/* Breadcrumbs */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            color: "#104145",
            gap: 1,
          }}
          className="mobile-breadcrumbs"
        >
          <ArrowBackIosNewIcon
            sx={{
              cursor: "pointer",
              "&:hover": {
                opacity: 0.8,
              },
            }}
            onClick={() => navigate(-1)}
          />

          <Typography
            sx={{
              fontSize: "1rem",
              color: "#000",
            }}
          >
            List of Funeral Homes
          </Typography>
        </Box>

        <Box className="view-toggle">
          <Button
            onClick={() => setListView((prev) => !prev)}
            variant={listView ? "contained" : "outlined"}
            className={`toggle-btn ${listView ? "active" : ""}`}
          >
            <ViewListRoundedIcon fontSize="small" />
          </Button>
          <Button
            onClick={() => setListView((prev) => !prev)}
            variant={!listView ? "contained" : "outlined"}
            className={`toggle-btn ${!listView ? "active" : ""}`}
          >
            <GridViewOutlinedIcon fontSize="small" />
          </Button>
        </Box>
      </Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Button
          sx={{
            backgroundColor: "rgba(16, 65, 69, 0.15)",
            color: "#104145",
            textTransform: "capitalize",
          }}
          onClick={() => {
            onCloseDrawer();
          }}
        >
          <SvgIcon
            component={FilterBtnIcon}
            inheritViewBox
            sx={{
              width: "14px",
              marginRight: "0.3rem",
            }}
          />
          Filters
        </Button>
        <Button
          variant="text"
          onClick={() => {
            resetFilters();
            setPriceSlider(0);
            setDistanceSlider(0);
          }}
          sx={{
            color: "#182D40",
            textTransform: "capitalize",
            fontWeight: 500,
            fontSize: "14px",
          }}
        >
          <SvgIcon
            component={ResetIcon}
            inheritViewBox
            sx={{
              marginRight: "0.4rem",
              width: "16px",
            }}
          />
          Reset
        </Button>
      </Box>

      {/* Need Type */}
      <Card className="card-component">
        <CardContent className="card-content-component">
          <Typography
            variant="subtitle1"
            fontWeight={600}
            gutterBottom
            className="card-content-title"
          >
            Need Type
          </Typography>
          <RadioGroup
            value={filters.needType}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                needType: e.target.value as "at_need" | "pre_need",
              }))
            }
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <FormControlLabel
              value="at_need"
              control={
                <Radio
                  sx={{
                    color: "#8f8f8f",
                    "&.Mui-checked": {
                      color: "#e9ae10",
                    },
                  }}
                />
              }
              label="Immediate Need"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: "14px",
                },
              }}
            />
            <FormControlLabel
              value="pre_need"
              control={
                <Radio
                  sx={{
                    color: "#8f8f8f",
                    "&.Mui-checked": {
                      color: "#e9ae10",
                    },
                  }}
                />
              }
              label="Preneed"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: "14px",
                  paddingRight: 3,
                },
              }}
            />
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Services */}
      <Card className="card-component" elevation={0}>
        <CardContent className="card-content-component">
          <Typography
            variant="subtitle1"
            fontWeight={600}
            gutterBottom
            className="card-content-title"
          >
            Service Categories
          </Typography>
          <FormGroup
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              columnGap: 2,
            }}
          >
            {allCategories?.map((category) => (
              <FormControlLabel
                key={category.id}
                control={
                  <Checkbox
                    name={category.id.toString()}
                    checked={filters.services[category.id] || false}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        services: {
                          ...prev.services,
                          [category.id]: e.target.checked,
                        },
                      }))
                    }
                    sx={{
                      color: "#8f8f8f",
                      "&.Mui-checked": {
                        color: "#e9ae10",
                      },
                    }}
                  />
                }
                label={category.name}
                className="services-form-control-label"
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "14px",
                  },
                }}
              />
            ))}
          </FormGroup>
        </CardContent>
      </Card>

      {/* Price */}
      <Card className="card-component">
        <CardContent className="card-content-component">
          <Typography
            variant="subtitle1"
            fontWeight={600}
            gutterBottom
            className="card-content-title"
          >
            Price Range
          </Typography>
          <Box display="flex" gap={2} mb={2}>
            <TextField
              placeholder="Min"
              type="number"
              value={
                activePriceField !== null && filters.priceRange
                  ? filters.priceRange[0]
                  : ""
              }
              onFocus={() => {
                setActivePriceField("min");
                if (filters.priceRange) {
                  setPriceSlider(filters.priceRange[0]);
                } else {
                  setPriceSlider(0); // set to 0 if not set
                }
              }}
              onChange={(e) =>
                setFilters((prev) => {
                  const [min = 0, max = 7000] = prev.priceRange ?? [];
                  return {
                    ...prev,
                    priceRange: [Number(e.target.value), max],
                  };
                })
              }
              size="small"
            />

            <TextField
              placeholder="Max"
              type="number"
              value={
                activePriceField !== null && filters.priceRange
                  ? filters.priceRange[1]
                  : ""
              }
              onFocus={() => {
                setActivePriceField("max");
                if (filters.priceRange) {
                  setPriceSlider(filters.priceRange[1]);
                } else {
                  setPriceSlider(7000); // set 7000 if not set
                }
              }}
              onChange={(e) =>
                setFilters((prev) => {
                  const [min = 0, max = 7000] = prev.priceRange ?? [];
                  return {
                    ...prev,
                    priceRange: [min, Number(e.target.value)],
                  };
                })
              }
              size="small"
            />
          </Box>
          <Slider
            value={priceSlider}
            onChange={(_, val) => {
              const newVal = val as number;
              setPriceSlider(newVal);
              setFilters((prev) => {
                const [min = 0, max = 7000] = prev.priceRange ?? [];
                return {
                  ...prev,
                  priceRange:
                    activePriceField === "min" ? [newVal, max] : [min, newVal],
                };
              });
            }}
            valueLabelDisplay="auto"
            min={0}
            max={7000}
            step={100}
            sx={{
              "& .MuiSlider-thumb": {
                backgroundColor: "#fff",
              },
              "& .MuiSlider-rail": {
                backgroundColor: "#e2e8f0",
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Distance */}
      <Card className="card-component">
        <CardContent className="card-content-component">
          <Typography
            variant="subtitle1"
            fontWeight={600}
            gutterBottom
            className="card-content-title"
          >
            Distance
          </Typography>
          <Box display="flex" gap={2} mb={2}>
            <TextField
              placeholder="Min"
              type="number"
              value={
                activeDistanceField !== null && filters.distanceRange
                  ? filters.distanceRange[0]
                  : ""
              }
              onFocus={() => {
                setActiveDistanceField("min");
                if (filters.distanceRange) {
                  setDistanceSlider(filters.distanceRange[0]);
                } else {
                  setDistanceSlider(0); // fallback
                }
              }}
              onChange={(e) =>
                setFilters((prev) => {
                  const [min = 0, max = 100] = prev.distanceRange ?? [];
                  return {
                    ...prev,
                    distanceRange: [Number(e.target.value), max],
                  };
                })
              }
              size="small"
            />
            <TextField
              placeholder="Max"
              type="number"
              value={
                activeDistanceField !== null && filters.distanceRange
                  ? filters.distanceRange[1]
                  : ""
              }
              onFocus={() => {
                setActiveDistanceField("max");
                if (filters.distanceRange) {
                  setDistanceSlider(filters.distanceRange[1]);
                } else {
                  setDistanceSlider(100); // fallback
                }
              }}
              onChange={(e) =>
                setFilters((prev) => {
                  const [min = 0, max = 100] = prev.distanceRange ?? [];
                  return {
                    ...prev,
                    distanceRange: [min, Number(e.target.value)],
                  };
                })
              }
              size="small"
            />
          </Box>
          <Slider
            value={distanceSlider}
            onChange={(_, val) => {
              const newVal = val as number;
              setDistanceSlider(newVal);
              setFilters((prev) => {
                const [min = 0, max = 100] = prev.distanceRange ?? [];
                return {
                  ...prev,
                  distanceRange:
                    activeDistanceField === "min"
                      ? [newVal, max]
                      : [min, newVal],
                };
              });
            }}
            valueLabelDisplay="auto"
            min={0}
            max={100}
            step={5}
            sx={{
              "& .MuiSlider-thumb": {
                backgroundColor: "#fff",
              },
              "& .MuiSlider-rail": {
                backgroundColor: "#e2e8f0",
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Star Rating */}
      <Card className="card-component">
        <CardContent className="card-content-component">
          <Typography
            variant="subtitle1"
            fontWeight={600}
            gutterBottom
            className="card-content-title"
          >
            Star Rating
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {/* Top Rated Button */}
            <Button
              sx={{
                border: "1px solid #BFC0C2",
                color:
                  filters.sortBy === "rating_high_to_low" ? "#fff" : "#4B4F50",
                bgcolor:
                  filters.sortBy === "rating_high_to_low"
                    ? "#ebd28d"
                    : "transparent",
                textTransform: "capitalize",
              }}
              endIcon={
                <StarRoundedIcon
                  sx={{
                    color:
                      filters.sortBy !== "rating_high_to_low"
                        ? "#9aa0a8"
                        : "#FFF",
                  }}
                />
              }
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  sortBy:
                    prev.sortBy === "rating_high_to_low"
                      ? undefined
                      : "rating_high_to_low",
                }))
              }
            >
              Top Rated
            </Button>

            {/* Star Rating Buttons */}
            {[5, 4, 3].map((star) => {
              const isSelected = filters.ratings?.includes(star);
              return (
                <Button
                  key={star}
                  sx={{
                    border: "1px solid #BFC0C2",
                    color: isSelected ? "#FFF" : "#4B4F50",
                    bgcolor: isSelected ? "#ebd28d" : "transparent",
                    textTransform: "capitalize",
                  }}
                  onClick={() =>
                    setFilters((prev) => {
                      const current = prev.ratings || [];
                      return {
                        ...prev,
                        ratings: isSelected
                          ? current.filter((r) => r !== star)
                          : [...current, star],
                      };
                    })
                  }
                  endIcon={
                    <StarRoundedIcon
                      sx={{ color: isSelected ? "#fff" : "#9aa0a8" }}
                    />
                  }
                >
                  {star}
                </Button>
              );
            })}

            {/* Premium Button */}
            <Button
              sx={{
                border: "1px solid #BFC0C2",
                color: "#4B4F50",
                textTransform: "capitalize",
              }}
              endIcon={<SvgIcon component={Premium} inheritViewBox />}
            >
              Premium
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
